import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ArrowDownLeft, CheckCircle2, Calendar, Linkedin, Eye, Heart, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { initAttribution, getAttribution } from '../lib/attribution';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDIA_REVENUE = [
  'Below ₹10 Cr', '₹10–25 Cr', '₹25–50 Cr', '₹50–100 Cr', '₹100 Cr+'
];
const SG_REVENUE = [
  'Below SGD 5M', 'SGD 5–10M', 'SGD 10–25M', 'SGD 25–50M', 'SGD 50M+'
];

/* ─── Scroll-reveal hook ───
   Content is always visible in DOM. This just adds the slide-up animation class
   when the element scrolls into view. Safe fallback: force-reveals after 800ms
   so headless browsers (PDF export, link-preview bots) still get the animated class. */
const useScrollReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reveal = () => el.classList.add('anim-visible');
    // Fallback in case IntersectionObserver never fires (PDF/print/headless)
    const fallbackTimer = setTimeout(reveal, 800);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          clearTimeout(fallbackTimer);
          reveal();
          observer.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => { clearTimeout(fallbackTimer); observer.disconnect(); };
  }, []);
  return ref;
};

const AnimSection = ({ children, className = '', ...props }) => {
  const ref = useScrollReveal();
  return <section ref={ref} className={`anim-in ${className}`} {...props}>{children}</section>;
};

const HomePage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', service: '', geography: '', expansion_stage: '', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [showFrameworkModal, setShowFrameworkModal] = useState(false);
  const [frameworkForm, setFrameworkForm] = useState({ name: '', email: '' });
  const [frameworkSubmitting, setFrameworkSubmitting] = useState(false);

  useEffect(() => {
    // Initialise UTM attribution tracking (parses URL, persists to sessionStorage, resolves anchor aliases)
    initAttribution();

    const fetchBlogs = async () => {
      try {
        const res = await axios.get(`${API}/blogs?limit=3&featured=true`);
        if (res.data.success) setBlogPosts(res.data.blogs);
      } catch (e) { console.error('Failed to fetch blogs:', e); }
      finally { setBlogsLoading(false); }
    };
    fetchBlogs();
  }, []);

  const revenueOptions = form.geography === 'India' ? INDIA_REVENUE
    : (form.geography === 'Singapore' || form.geography === 'ASEAN (Other)') ? SG_REVENUE
    : [...INDIA_REVENUE, ...SG_REVENUE];

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) { toast.error('Please enter a valid email address'); return; }
    // Phone is optional. If provided, validate the format; if empty, skip validation.
    const cleanedPhone = form.phone.replace(/[\s\-\(\)]/g, '');
    if (cleanedPhone && !/^\+[1-9]\d{6,14}$/.test(cleanedPhone)) {
      toast.error('Please enter a valid phone number with country code (e.g., +91 98765 43210) or leave it blank');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/consultation/book`, { ...form, phone: cleanedPhone, ...getAttribution() });
      if (res.data.success) {
        toast.success(res.data.message, { duration: 5000, description: "We'll be in touch within 24 hours." });
        setForm({ name: '', email: '', phone: '', service: '', geography: '', expansion_stage: '', message: '' });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally { setLoading(false); }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(leadForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLeadSubmitting(true);
    try {
      const res = await axios.post(`${API}/corridor-readiness/lead`, { ...leadForm, ...getAttribution() });
      if (res.data.success) {
        toast.success(res.data.message, { duration: 6000 });
        setLeadForm({ name: '', email: '' });
        setShowLeadModal(false);
        // Immediate value: scroll to the Growth & Market Entry Program section
        setTimeout(() => scrollTo('signature-offer'), 200);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  const handleFrameworkSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(frameworkForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setFrameworkSubmitting(true);
    try {
      const res = await axios.post(`${API}/framework/lead`, { ...frameworkForm, ...getAttribution() });
      if (res.data.success) {
        toast.success(res.data.message, { duration: 6000 });
        setFrameworkForm({ name: '', email: '' });
        setShowFrameworkModal(false);
        setTimeout(() => scrollTo('contact'), 200);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setFrameworkSubmitting(false);
    }
  };

  return (
    <div className="dark-container">

      {/* ════════════════════════════════════════
          1. HERO — Clean & Standalone
          ════════════════════════════════════════ */}
      <section id="hero" className="hero-section" data-testid="hero-section">
        <div className="hero-content">
          <div className="hero-badge" data-testid="hero-badge">India–Singapore Growth, Capital &amp; M&amp;A Advisory</div>
          <h1 className="hero-title" data-testid="hero-title">
            <span className="hero-line">
              Cross-border deals. One advisor. <span className="hero-highlight">End&nbsp;to&nbsp;end&nbsp;ownership.</span>
            </span>
          </h1>
          <p className="hero-description" data-testid="hero-subhead">
            Bizenntrum helps growth-stage SMEs and startups expand between India and Singapore, using Singapore as a gateway to ASEAN, structure capital efficiently, and execute transactions with single-point accountability.
          </p>
          <p className="hero-description" style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '16px', marginBottom: '6px' }}>
            Big4 Experience. Founder Mindset. Cross-Border Execution.
          </p>
          <p className="hero-description" style={{ fontSize: '15px', marginBottom: '32px' }}>
            We don&apos;t just advise—we design and execute cross-border growth structures as one accountable partner.
          </p>
          <div className="hero-buttons">
            <button onClick={() => scrollTo('contact')} className="btn-primary" data-testid="hero-cta-primary">
              Book a Strategy Call <ArrowRight size={20} />
            </button>
            <button
              onClick={() => setShowLeadModal(true)}
              className="btn-secondary"
              data-testid="hero-cta-onepager"
            >
              Get the Growth & Market Entry One-Pager
            </button>
          </div>
          <button
            type="button"
            className="hero-qualifier-link"
            onClick={() => scrollTo('who-we-work-with')}
            data-testid="hero-qualifier-link"
          >
            Is Bizenntrum right for you?
          </button>
          <div className="credibility-strip" data-testid="credibility-strip">
            <span><strong style={{ color: 'var(--text-secondary)' }}>Cross-Border Transactions &amp; Fundraising</strong></span>
            <span className="strip-dot">·</span>
            <span>India–Singapore–ASEAN Execution</span>
            <span className="strip-dot">·</span>
            <span>Big4 Advisory Background (EY · KPMG · PwC)</span>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          2. WHO WE WORK WITH
          ════════════════════════════════════════ */}
      <AnimSection id="who-we-work-with" className="about-section" data-testid="who-section">
        <div className="about-content">
          <div className="section-header">
            <div className="section-divider" />
            <h2 className="section-title">Who We Work With</h2>
            <p className="section-subtitle">
              Bizenntrum works with founder & owner-led SMEs and startups where growth has outpaced structure.
            </p>
          </div>
          <div className="about-highlights">
            <div className="highlight-card" data-testid="who-india-card">
              <div className="highlight-icon" aria-hidden="true" data-testid="who-india-icon">
                <ArrowUpRight size={22} />
              </div>
              <h3 className="highlight-title">For India-Based Founder & Owner-Led Businesses</h3>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '12px' }}>We are a fit if you are:</p>
              <ul className="highlight-list">
                <li>Planning your next phase of growth</li>
                <li>Preparing to raise strategic or institutional capital</li>
                <li>Exploring a partial or full business sale</li>
                <li>Looking for Singapore or ASEAN investors, buyers or strategic partners</li>
                <li>Strengthening financial, governance and transaction readiness</li>
              </ul>
            </div>
            <div className="highlight-card highlight-card--sg" data-testid="who-singapore-card">
              <div className="highlight-icon" aria-hidden="true" data-testid="who-singapore-icon">
                <ArrowDownLeft size={22} />
              </div>
              <h3 className="highlight-title">For Singapore-Based Funded Companies</h3>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '12px' }}>We are a fit if you are:</p>
              <ul className="highlight-list">
                <li>Evaluating India market entry or expansion</li>
                <li>Considering whether to build, partner or acquire</li>
                <li>Deploying institutional, venture or strategic capital for India expansion</li>
                <li>Navigating India&apos;s regulatory, tax and execution landscape with confidence</li>
              </ul>
            </div>
          </div>
          <p className="hero-target-audience" style={{ marginTop: '32px' }}>
            Typical client profile: ₹10–100 Cr (India) | SGD 5–50M (Singapore & ASEAN)
          </p>
        </div>
      </AnimSection>

      {/* ════════════════════════════════════════
          3. WHY CROSS-BORDER EXPANSION GETS EXPENSIVE
          ════════════════════════════════════════ */}
      <AnimSection id="challenges" className="services-section" data-testid="challenges-section">
        <div className="section-header">
          <div className="section-divider" />
          <h2 className="section-title">Why Cross-Border Expansion Gets Expensive</h2>
          <p className="section-subtitle">
            Most cross-border expansions become costly not because of ambition, but because structure and execution are not aligned.
          </p>
        </div>
        <div className="challenges-card">
          <ul className="challenges-list">
            <li><CheckCircle2 size={18} className="challenge-icon" /> Incorrect entity structure leads to long-term tax inefficiency</li>
            <li><CheckCircle2 size={18} className="challenge-icon" /> Lack of capital strategy delays or weakens fundraising outcomes</li>
            <li><CheckCircle2 size={18} className="challenge-icon" /> Weak financial systems reduce investor and lender confidence</li>
            <li><CheckCircle2 size={18} className="challenge-icon" /> Regulatory missteps require costly restructuring later</li>
            <li><CheckCircle2 size={18} className="challenge-icon" /> Multiple advisors without ownership slow down execution</li>
            <li><CheckCircle2 size={18} className="challenge-icon" /> Deals stall without integrated execution across finance, tax, and legal</li>
          </ul>
          <p className="challenges-bridge">
            Bizenntrum integrates strategy, structure, and execution under one framework—and takes ownership across finance, tax, and legal coordination so early decisions support long-term scale.
          </p>
        </div>
      </AnimSection>

      {/* ════════════════════════════════════════
          4. HOW BIZENNTRUM HELPS (3 pillars)
          ════════════════════════════════════════ */}
      <AnimSection id="services" className="about-section" data-testid="services-section">
        <div className="about-content">
          <div className="section-header">
            <div className="section-divider" />
            <h2 className="section-title">How Bizenntrum Helps</h2>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-card-content">
                <div className="service-number">01</div>
                <h3 className="service-title" style={{ color: 'var(--brand-blue)' }}>Expansion Architecture</h3>
                <p className="service-subtitle">Design the right structure before entering a new market.</p>
                <ul className="service-features">
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-blue)' }} /><span>India ↔ Singapore structuring</span></li>
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-blue)' }} /><span>Holding company and subsidiary setup</span></li>
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-blue)' }} /><span>Market entry sequencing and strategy</span></li>
                </ul>
              </div>
            </div>
            <div className="service-card">
              <div className="service-card-content">
                <div className="service-number">02</div>
                <h3 className="service-title" style={{ color: 'var(--brand-green)' }}>Capital & Financial Readiness</h3>
                <p className="service-subtitle">Build clarity, control, and investor confidence.</p>
                <ul className="service-features">
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-green)' }} /><span>Executive-Level Performance Visibility &amp; Financial Systems</span></li>
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-green)' }} /><span>Capital Strategy &amp; Funding Readiness</span></li>
                </ul>
                <button
                  type="button"
                  onClick={() => scrollTo('framework')}
                  className="framework-link-pill"
                  data-testid="framework-link-pill"
                  aria-label="Part of the 10 to 100 CFO System — jump to framework section"
                >
                  Part of the 10→100 CFO System <ArrowRight size={12} />
                </button>
              </div>
            </div>
            <div className="service-card">
              <div className="service-card-content">
                <div className="service-number">03</div>
                <h3 className="service-title" style={{ color: 'var(--brand-pink)' }}>Tax, Governance &amp; Risk</h3>
                <p className="service-subtitle">Build defensible foundations across tax, compliance and governance.</p>
                <ul className="service-features">
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-pink)' }} /><span>Cross-border tax structuring, transfer pricing &amp; transaction tax</span></li>
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-pink)' }} /><span>Legal and regulatory coordination</span></li>
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-pink)' }} /><span>Governance frameworks and internal controls</span></li>
                </ul>
              </div>
            </div>
            <div className="service-card" data-testid="ma-pillar-card">
              <div className="service-card-content">
                <div className="service-number">04</div>
                <h3 className="service-title" style={{ color: 'var(--brand-blue)' }}>M&amp;A &amp; Strategic Growth Advisory</h3>
                <p className="service-subtitle">Execute the transaction, not just the strategy.</p>
                <ul className="service-features">
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-blue)' }} /><span>Buy-side and sell-side M&amp;A advisory</span></li>
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-blue)' }} /><span>Joint venture and strategic partnership structuring</span></li>
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-blue)' }} /><span>Deal negotiation and term sheet support</span></li>
                  <li className="service-feature"><CheckCircle2 size={16} style={{ color: 'var(--brand-blue)' }} /><span>Vendor due diligence and closing readiness</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </AnimSection>

      {/* ════════════════════════════════════════
          4b. SIGNATURE OFFER — India–Singapore Growth & Market Entry Program
          Positioned BEFORE the 10→100 CFO System — the what we help clients achieve.
          ════════════════════════════════════════ */}
      <AnimSection id="signature-offer" className="signature-section" data-testid="signature-offer-section">
        <div className="section-header">
          <div className="section-divider" />
          <h2 className="section-title">India–Singapore Growth &amp; Market Entry Program</h2>
          <p className="section-subtitle">
            A structured strategic engagement that helps founder & owner-led businesses expand across the India–Singapore corridor through market entry, strategic partnerships, acquisitions and long-term growth.
          </p>
          <p className="flagship-connector" data-testid="flagship-connector">
            Our flagship cross-border engagement, powered by the 10→100 CFO System.
          </p>
        </div>
        <div className="signature-grid">
          <div className="signature-block">
            <h3 className="highlight-title">Who This Is For</h3>
            <ul className="highlight-list">
              <li>SMEs expanding between India and Singapore</li>
              <li>Businesses preparing for a capital raise</li>
              <li>Founders needing clarity before committing capital to a new market</li>
            </ul>
          </div>
          <div className="signature-block">
            <h3 className="highlight-title">What You Get</h3>
            <ul className="highlight-list">
              <li>Expansion structure blueprint</li>
              <li>Capital strategy roadmap</li>
              <li>Tax and regulatory overview</li>
              <li>Financial and governance gap assessment</li>
            </ul>
          </div>
          <div className="signature-block">
            <h3 className="highlight-title">Engagement Format</h3>
            <ul className="highlight-list">
              <li>2–4 weeks</li>
              <li>Fixed-fee, outcome-driven</li>
              <li>Led by senior advisory</li>
            </ul>
          </div>
        </div>
        <div className="hero-buttons" style={{ marginTop: '40px' }}>
          <button onClick={() => scrollTo('contact')} className="btn-primary" data-testid="signature-cta-primary">
            Explore Growth &amp; Market Entry Program <ArrowRight size={20} />
          </button>
          <button onClick={() => scrollTo('contact')} className="btn-secondary" data-testid="signature-cta-secondary">
            Book a Strategy Call
          </button>
        </div>
      </AnimSection>

      {/* ════════════════════════════════════════
          4c. NARRATIVE BRIDGE — connects Growth Program to the 10→100 CFO System
          ════════════════════════════════════════ */}
      <AnimSection className="framework-bridge" data-testid="framework-bridge">
        <p className="framework-bridge-text">
          Successful cross-border growth starts long before the first investment, acquisition or market entry decision. It depends on having the right financial leadership, governance and capital readiness already in place. That&apos;s why every Bizenntrum engagement is built on the <strong>10→100 CFO System</strong> — our signature operating framework that helps founder & owner-led businesses build scalable financial systems, strengthen governance, prepare for capital, and execute sustainable growth with confidence.
        </p>
      </AnimSection>

      {/* ════════════════════════════════════════
          4d. SIGNATURE FRAMEWORK — 10→100 CFO System
          The "how" — our proprietary operating framework.
          ════════════════════════════════════════ */}
      <AnimSection id="framework" className="framework-section" data-testid="framework-section">
        <div className="framework-badge" data-testid="framework-badge">Signature Framework</div>
        <h2 className="framework-heading" data-testid="framework-heading">
          <span className="framework-num">10→100</span> CFO System
        </h2>
        <p className="framework-subheading" data-testid="framework-subheading">
          A structured operating system that helps founder & owner-led businesses build the financial leadership, governance, capital readiness and execution capabilities required to scale with confidence.
        </p>

        <div className="framework-grid">
          <div className="framework-card" data-testid="framework-card-audience">
            <div className="framework-card-eyebrow">Who This Is For</div>
            <ul>
              <li>Founder & owner-led businesses entering their next stage of growth (typically ₹10–100 Cr in India or SGD 5–50M in Singapore &amp; ASEAN).</li>
              <li>Companies that have outgrown founder & owner-led finance but are not yet ready for a full-time CFO</li>
              <li>Businesses preparing for institutional capital, acquisitions or board-level governance</li>
            </ul>
          </div>
          <div className="framework-card" data-testid="framework-card-what-you-get">
            <div className="framework-card-eyebrow">What You Get</div>
            <ul>
              <li>Executive-Level Performance Visibility &amp; Financial Systems</li>
              <li>Capital Readiness for Investors, Lenders &amp; Acquirers</li>
              <li>Governance Frameworks that Scale with the Business</li>
              <li>One Integrated Operating System for Growth, Capital &amp; Transactions</li>
            </ul>
          </div>
          <div className="framework-card" data-testid="framework-card-engagement">
            <div className="framework-card-eyebrow">Engagement Format</div>
            <ul>
              <li>Fixed-term implementation (typically 4–8 weeks)</li>
              <li>Led by senior advisory professionals</li>
              <li>Designed to transition naturally into Strategic Growth, Capital or M&amp;A engagements</li>
            </ul>
          </div>
        </div>

        <div className="framework-cta-row">
          <button
            type="button"
            onClick={() => setShowFrameworkModal(true)}
            className="btn-primary"
            data-testid="framework-cta-primary"
          >
            See How the 10→100 CFO System Works <ArrowRight size={18} />
          </button>
          <button
            type="button"
            onClick={() => scrollTo('contact')}
            className="btn-secondary"
            data-testid="framework-cta-secondary"
          >
            Book a Strategy Call
          </button>
        </div>
      </AnimSection>

      {/* ════════════════════════════════════════
          6. FOR SINGAPORE BUSINESSES ENTERING INDIA
          ════════════════════════════════════════ */}
      <AnimSection id="singapore-india" className="about-section" data-testid="singapore-india-section">
        {/* Anchor alias — used by SG outreach UTM landing URLs (#india-entry) */}
        <span id="india-entry" aria-hidden="true" style={{ position: 'absolute', marginTop: '-80px' }} />
        <div className="about-content">
          <div className="section-header">
            <div className="section-divider" />
            <h2 className="section-title">For Singapore Businesses Entering India</h2>
            <p className="section-subtitle" style={{ fontWeight: 600, color: 'var(--text-primary)' }} data-testid="sg-india-decision-line">
              Decide whether to build, partner or acquire in India—before committing significant capital.
            </p>
            <p className="section-subtitle" style={{ marginTop: '10px' }}>Entering India? Structure It Right from Day One.</p>
          </div>
          <div className="sg-india-content">
            <p className="about-description">
              Bizenntrum supports Singapore-based SMEs and startups planning to expand into India with the right structure, financial planning, and execution roadmap.
            </p>
            <div className="sg-india-card">
              <h3 className="highlight-title">We help you</h3>
              <ul className="highlight-list">
                <li>Evaluate India market entry strategy</li>
                <li>Set up legal and financial structure</li>
                <li>Navigate tax and regulatory environment</li>
                <li>Identify operational and partner risks</li>
                <li>Deploy capital efficiently</li>
              </ul>
            </div>
            <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', textAlign: 'center', marginTop: '24px' }}>
              Deep on-ground India execution insight—not just strategy.
            </p>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', textAlign: 'center', marginTop: '8px', fontStyle: 'italic' }}>
              Well suited to companies undertaking structured overseas expansion planning and internationalisation initiatives.
            </p>
          </div>
        </div>
      </AnimSection>

      {/* ════════════════════════════════════════
          7. CREDIBILITY — Experience You Can Trust (standalone)
          ════════════════════════════════════════ */}
      <AnimSection id="credibility" className="services-section" data-testid="credibility-section">
        <div className="section-header">
          <div className="section-divider" />
          <h2 className="section-title">Experience You Can Trust</h2>
          <p className="section-subtitle" style={{ fontWeight: 600, color: 'var(--brand-green)' }}>
            Single-point accountability across finance, tax, and legal execution.
          </p>
        </div>
        <div className="about-highlights" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div className="highlight-card">
            <h3 className="highlight-title">What We Bring to the Table</h3>
            <ul className="highlight-list">
              <li>Leadership experience across Big4 ecosystems</li>
              <li>Cross-border transactions and fundraising mandates</li>
              <li>Entrepreneurial experience building and scaling ventures</li>
            </ul>
          </div>
          <div className="highlight-card">
            <h3 className="highlight-title">What Makes Bizenntrum Different</h3>
            <ul className="highlight-list">
              <li>Strategy plus execution—beyond pure advisory</li>
              <li>Understanding of both founder and investor perspectives</li>
              <li>Operating understanding of India and Singapore environments</li>
              <li>Integrated coordination across finance, tax, and legal domains</li>
              <li>Designed to withstand investor, regulatory, and transfer pricing scrutiny</li>
            </ul>
          </div>
        </div>
      </AnimSection>

      {/* ════════════════════════════════════════
          8. LEADERSHIP — Viikas + Pankaj (two Partners)
          ════════════════════════════════════════ */}
      <AnimSection id="team" className="founder-standalone" data-testid="leadership-section">
        <div className="section-header">
          <div className="section-divider" />
          <h2 className="section-title">Leadership</h2>
        </div>
        <div className="leadership-grid" data-testid="leadership-grid">
          <div className="leadership-card" data-testid="leadership-card-viikas">
            <img
              src="https://customer-assets.emergentagent.com/job_sg-india-bridge/artifacts/97qht7su_ChatGPT%20Image%20Jan%2024%2C%202026%2C%2006_48_16%20AM.png"
              alt="Viikas P Sethia"
              className="leadership-photo"
            />
            <p className="leadership-name" data-testid="founder-name">Viikas P. Sethia</p>
            <p className="leadership-role">Partner</p>
            <p className="leadership-domain">Growth, Capital &amp; M&amp;A</p>
            <p className="leadership-bio" data-testid="founder-bio">Former EY and KPMG advisor. Chartered Accountant. Startup founder. Independent Director. Viikas advises founder &amp; owner-led businesses on strategic growth, cross-border expansion, M&amp;A and capital strategy across the India–Singapore–ASEAN corridor as Bizenntrum&apos;s one accountable partner, from strategy through execution.</p>
            <a href="https://www.linkedin.com/in/viikaspsethia" target="_blank" rel="noopener noreferrer" className="team-linkedin-link">
              <Linkedin size={16} /> View Profile
            </a>
          </div>
          <div className="leadership-card" data-testid="leadership-card-pankaj">
            <img
              src="https://customer-assets.emergentagent.com/job_sg-ind-experts/artifacts/t6ty12gp_Pankaj%20Sancheti.png"
              alt="Pankaj Sancheti"
              className="leadership-photo"
            />
            <p className="leadership-name">Pankaj Sancheti</p>
            <p className="leadership-role">Partner</p>
            <p className="leadership-domain">International Tax &amp; Cross-Border Structuring</p>
            <p className="leadership-bio">Chartered Accountant with over two decades of experience in international tax, transfer pricing and cross-border structuring. Former Partner at PwC, with more than 12 years at EY.</p>
            <a href="https://www.linkedin.com/in/pankaj-sancheti-9111297/" target="_blank" rel="noopener noreferrer" className="team-linkedin-link">
              <Linkedin size={16} /> View Profile
            </a>
          </div>
        </div>
      </AnimSection>

      {/* ════════════════════════════════════════
          9. SPECIALIST ADVISORY NETWORK — Prashant + Priyanka
          ════════════════════════════════════════ */}
      <AnimSection className="network-standalone" data-testid="network-section">
        <div className="network-section">
          <div className="section-header">
            <div className="section-divider" />
            <h2 className="section-title" style={{ fontSize: '32px' }}>Specialist Advisory Network</h2>
            <p className="section-subtitle" style={{ fontSize: '16px' }}>
              Bizenntrum leads client strategy and coordinates execution through specialist and affiliated partners across legal, accounting and compliance services.
            </p>
          </div>
          <div className="team-names-grid">
            <div className="team-name-card" data-testid="network-card-prashant">
              <div className="team-card-header">
                <img
                  src="https://customer-assets.emergentagent.com/job_sg-ind-experts/artifacts/rlgd1ghe_Prashant%20Jain%20%282%29.png"
                  alt="Prashant Jain"
                  className="team-member-photo"
                />
                <div className="team-card-info">
                  <p style={{ fontSize: '12px', color: 'var(--brand-green)', fontWeight: 600, marginBottom: '2px' }}>Corporate &amp; Transaction Law</p>
                  <p className="team-member-name">Prashant Jain</p>
                  <p className="team-member-domain">Founder, <a href="https://samistilegal.in/" target="_blank" rel="noopener noreferrer" className="samisti-link">Samisti Legal</a></p>
                </div>
              </div>
              <p className="team-member-bio">Corporate lawyer with nearly two decades of experience across approximately 350 transactions, advising growth-stage businesses on M&amp;A, fundraising, corporate structuring and cross-border investments.</p>
              <a href="https://www.linkedin.com/in/prashant-jain-68311a18/" target="_blank" rel="noopener noreferrer" className="team-linkedin-link">
                <Linkedin size={16} /> View Profile
              </a>
            </div>
            <div className="team-name-card" data-testid="network-card-priyanka">
              <div className="team-card-header">
                <img
                  src="https://customer-assets-39nsmqrw.emergentagent.net/job_5a776a7b-b0e2-409c-8aa2-249a0831c628/artifacts/h5gd7j0d_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2010_55_31%20PM.png"
                  alt="Priyanka Golchha, Partner at Ratnatrraya Consulting LLP"
                  className="team-member-photo"
                />
                <div className="team-card-info">
                  <p style={{ fontSize: '12px', color: 'var(--brand-green)', fontWeight: 600, marginBottom: '2px' }}>Accounting &amp; India Compliance</p>
                  <p className="team-member-name">Priyanka Golchha</p>
                  <p className="team-member-domain">Partner, Ratnatrraya Consulting LLP</p>
                </div>
              </div>
              <p className="team-member-bio">Chartered Accountant supporting growth-stage and India-entry businesses with accounting setup, financial reporting, business registrations, domestic tax compliance and ongoing finance operations.</p>
              <p className="network-affiliation-label" data-testid="priyanka-affiliation">Affiliated Execution Partner</p>
              <a href="https://ratnatrraya.consulting/" target="_blank" rel="noopener noreferrer" className="team-linkedin-link" data-testid="priyanka-cta">
                <ExternalLink size={16} /> Visit Partner Website
              </a>
            </div>
          </div>
        </div>
      </AnimSection>

      {/* ════════════════════════════════════════
          10. INSIGHTS
          ════════════════════════════════════════ */}
      <AnimSection id="resources" className="resources-section" data-testid="insights-section">
        <div className="section-header">
          <div className="section-divider" />
          <h2 className="section-title">Founder Insights: India–Singapore Growth</h2>
          <p className="section-subtitle">
            Stay informed with practical insights on cross-border expansion, capital strategy, and governance for founder & owner-led businesses.
          </p>
        </div>
        <div className="blog-grid">
          {blogsLoading ? (
            <div className="empty-state">Loading insights...</div>
          ) : blogPosts.length === 0 ? (
            <div className="empty-state"><p>New insights coming soon. Stay tuned!</p></div>
          ) : (
            blogPosts.map((post) => (
              <div key={post.id} className="blog-card" data-testid={`blog-card-${post.id}`}>
                {post.featured_image && (
                  <div className="blog-card-image"><img src={post.featured_image} alt={post.title} /></div>
                )}
                <div className="blog-card-content">
                  <div className="blog-meta">
                    <span className="blog-category">{post.category}</span>
                    <span className="blog-date">
                      {post.published_date ? new Date(post.published_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent'}
                    </span>
                  </div>
                  <h3 className="blog-title">{post.title}</h3>
                  <p className="blog-excerpt">{post.excerpt}</p>
                  <div className="blog-footer">
                    <span className="blog-author">By {post.author || 'Bizenntrum Team'}</span>
                    <span className="blog-read-time">{post.read_time || '5 min read'}</span>
                  </div>
                  <div className="blog-engagement-stats">
                    <span className="engagement-stat"><Eye size={14} /> {post.views || 0}</span>
                    <span className="engagement-stat"><Heart size={14} /> {post.likes || 0}</span>
                  </div>
                  <button className="blog-cta" data-testid={`read-more-${post.id}`} onClick={() => navigate(`/blog/${post.slug}`)}>
                    Read More <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </AnimSection>

      {/* ════════════════════════════════════════
          11. FINAL CTA + FORM
          ════════════════════════════════════════ */}
      <section id="contact" className="contact-section" data-testid="contact-section">
        {/* Anchor alias — used by India outreach UTM landing URLs (#strategy-call) */}
        <span id="strategy-call" aria-hidden="true" style={{ position: 'absolute', marginTop: '-80px' }} />
        <div className="contact-wrapper">
          <div className="contact-left">
            <h2 className="section-title">Plan Your Next Strategic Move</h2>
            <p className="contact-description">
              Schedule a structured 30-minute discussion to evaluate your expansion strategy, capital plans or strategic transaction across the India–Singapore corridor.
            </p>
            <p className="contact-description" style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '15px' }}>
              Designed for founders evaluating growth, capital, market entry or strategic transactions.
            </p>
            <ul className="contact-benefits">
              <li><CheckCircle2 size={20} className="benefit-icon" /> Expansion & Market Entry Readiness</li>
              <li><CheckCircle2 size={20} className="benefit-icon" /> Capital Strategy & Funding Alignment</li>
              <li><CheckCircle2 size={20} className="benefit-icon" /> Governance & Financial Control Architecture</li>
            </ul>
            <p className="contact-qualifier">
              Typically suited for SMEs in the ₹10–100 Cr and SGD 5–50M revenue range.
            </p>
          </div>

          <div className="contact-right">
            <div className="consultation-card">
              <div className="consultation-header">
                <Calendar size={28} />
                <h3 className="consultation-title">Book a Strategy Call</h3>
              </div>
              <p className="consultation-description">
                A structured 30-minute growth &amp; market entry readiness review covering expansion architecture, capital strategy, and governance controls.
              </p>
              <p className="consultation-typical-discussions" data-testid="typical-discussions-line">
                Typical discussions include India market entry, capital raises, strategic partnerships and founder exits.
              </p>
              <form onSubmit={handleSubmit} className="consultation-form" data-testid="strategy-call-form">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input type="text" className="form-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="form-name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required data-testid="form-email" />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone <span style={{ color: 'var(--text-muted, #9ca3af)', fontWeight: 400, fontSize: '13px' }}>(optional, with country code)</span></label>
                  <input type="tel" className="form-input" placeholder="+91 98765 43210 or +65 9876 5432" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="form-phone" />
                </div>
                <div className="form-group">
                  <label className="form-label">Geography</label>
                  <select className="form-select" value={form.geography} onChange={(e) => setForm({...form, geography: e.target.value, service: ''})} required data-testid="form-geography">
                    <option value="">Select geography</option>
                    <option value="India">India</option>
                    <option value="Singapore">Singapore</option>
                    <option value="ASEAN (Other)">ASEAN (Other)</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Revenue Range</label>
                  <select className="form-select" value={form.service} onChange={(e) => setForm({...form, service: e.target.value})} data-testid="form-revenue">
                    <option value="">Select revenue range</option>
                    {revenueOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Expansion Stage</label>
                  <select className="form-select" value={form.expansion_stage} onChange={(e) => setForm({...form, expansion_stage: e.target.value})} data-testid="form-stage">
                    <option value="">Select stage</option>
                    <option value="Exploring">Exploring</option>
                    <option value="Planning">Planning</option>
                    <option value="Actively Executing">Actively Executing</option>
                    <option value="Already Operating Cross-Border">Already Operating Cross-Border</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Message (Optional)</label>
                  <textarea className="form-textarea" rows="3" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Tell us about your expansion plans..." data-testid="form-message" />
                </div>
                <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={loading} data-testid="form-submit-btn">
                  {loading ? 'Submitting...' : 'Book a Strategy Call'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Growth & Market Entry One-Pager — lead-capture modal */}
      {showLeadModal && (
        <div
          className="lead-modal-backdrop"
          onClick={() => setShowLeadModal(false)}
          data-testid="lead-modal-backdrop"
        >
          <div className="lead-modal" onClick={(e) => e.stopPropagation()} data-testid="lead-modal" role="dialog" aria-labelledby="lead-modal-title">
            <button
              type="button"
              className="lead-modal-close"
              onClick={() => setShowLeadModal(false)}
              aria-label="Close"
              data-testid="lead-modal-close"
            >
              <X size={20} />
            </button>
            <div className="lead-modal-eyebrow">Free Preview</div>
            <h3 id="lead-modal-title">Get the Growth &amp; Market Entry One-Pager</h3>
            <p>
              A concise preview of the India–Singapore Growth &amp; Market Entry Program — what it covers, who it&apos;s for, and how the engagement is structured. Sent straight to your inbox.
            </p>
            <form className="lead-modal-form" onSubmit={handleLeadSubmit}>
              <input
                type="text"
                placeholder="Your name (optional)"
                value={leadForm.name}
                onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                data-testid="lead-modal-name"
                autoComplete="name"
              />
              <input
                type="email"
                placeholder="Work email"
                value={leadForm.email}
                onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                required
                data-testid="lead-modal-email"
                autoComplete="email"
              />
              <button type="submit" disabled={leadSubmitting} data-testid="lead-modal-submit">
                {leadSubmitting ? 'Sending…' : 'Send Me the One-Pager'}
              </button>
            </form>
            <p className="lead-modal-fineprint">We&apos;ll never share your email. Unsubscribe anytime.</p>
          </div>
        </div>
      )}

      {/* 10→100 CFO System — framework overview lead-capture modal */}
      {showFrameworkModal && (
        <div
          className="lead-modal-backdrop"
          onClick={() => setShowFrameworkModal(false)}
          data-testid="framework-modal-backdrop"
        >
          <div
            className="lead-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="framework-modal"
            role="dialog"
            aria-labelledby="framework-modal-title"
          >
            <button
              type="button"
              className="lead-modal-close"
              onClick={() => setShowFrameworkModal(false)}
              aria-label="Close"
              data-testid="framework-modal-close"
            >
              <X size={20} />
            </button>
            <div className="lead-modal-eyebrow">Signature Framework</div>
            <h3 id="framework-modal-title">See How the 10→100 CFO System Works</h3>
            <p>
              Get the framework overview — how the 10→100 CFO System integrates financial leadership, capital readiness and governance into a single operating system. Sent to your inbox with next-step guidance.
            </p>
            <form className="lead-modal-form" onSubmit={handleFrameworkSubmit}>
              <input
                type="text"
                placeholder="Your name (optional)"
                value={frameworkForm.name}
                onChange={(e) => setFrameworkForm({ ...frameworkForm, name: e.target.value })}
                data-testid="framework-modal-name"
                autoComplete="name"
              />
              <input
                type="email"
                placeholder="Work email"
                value={frameworkForm.email}
                onChange={(e) => setFrameworkForm({ ...frameworkForm, email: e.target.value })}
                required
                data-testid="framework-modal-email"
                autoComplete="email"
              />
              <button type="submit" disabled={frameworkSubmitting} data-testid="framework-modal-submit">
                {frameworkSubmitting ? 'Sending…' : 'Send Me the Overview'}
              </button>
            </form>
            <p className="lead-modal-fineprint">We&apos;ll never share your email. Unsubscribe anytime.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
